import { defineStore } from 'pinia'
import { logger } from '@/utils/logger'
import { useChatbotStore, focusKey, type ChatFocusRef } from '@/stores/chatbot'
import { useChatScope } from '@/composables/useChatScope'
import { trackAction } from '@/composables/useActivityTracker'
import { CsmAction } from '@/constants/csmActions'
import type { LocationQuery } from 'vue-router'
import { PILOTAGE_DEFAULT_TAB, isPilotageRoute, tabOfLocation } from '@/utils/pilotageRoutes'

// MYAC-917 — UI state of the global chat panel (the "chat everywhere" surface,
// docked-only in production — the overlay/bottom-sheet/mode-toggle POC scaffolding
// was stripped after the A/B test settled on docked). Conversation state stays in
// stores/chatbot.ts; this store only owns the shell: open/closed and width.

export const MIN_PANEL_WIDTH = 340
export const MAX_PANEL_WIDTH = 720
const DEFAULT_WIDTH = 420

/**
 * MYAC-1013 — how wide the panel WANTS to be the first time a user opens it.
 *
 * It used to open at a flat 420px on every screen, which is where "the chat is
 * mushed, I have to widen it" came from: prompt suggestions are whole sentences
 * and replies carry cards and tables, none of which have room at 420. A 27"
 * monitor had ~1000px going spare and still got the cramped panel.
 *
 * So the opening width is now the widest COMFORTABLE one the screen can afford
 * without pushing page content under MIN_CONTENT_WIDTH — i.e. never at the cost
 * of flipping itself into the overlay regime, which is what a naive bigger
 * default would have done to 1152px laptops. Small screens still get their old
 * behaviour, large ones stop wasting the space.
 *
 * First open only: any width the user sets is persisted and wins forever after.
 */
const PREFERRED_WIDTH = 560

// MYAC-917 — responsive regimes, driven by MEASURED content width (not raw
// viewport): the docked regime needs at least MIN_CONTENT_WIDTH beside the
// panel to stay usable; below that it degrades to overlay. Below
// TAKEOVER_MAX_VIEWPORT the panel takes over the full viewport instead.
export const MIN_CONTENT_WIDTH = 700
export const TAKEOVER_MAX_VIEWPORT = 640

export type ChatPanelRegime = 'docked' | 'overlay' | 'takeover'


/**
 * MYAC-1484 — where the FLOATING panel (overlay / takeover) paints: over the
 * page, under every PrimeVue popup — menus, dropdowns, popovers, tooltips all
 * start at 1000.
 *
 * Left to PrimeVue, the Drawer takes the next MODAL value from its running
 * counter (1101 and climbing), above the header's submenus pinned at 1100. Any
 * part of the QMS / Products / Regulatory monitoring dropdown that crossed the
 * panel went under it — cut off, and the click landed in the chat. A popup
 * opened from the page sitting above the panel is also what the docked regime
 * has always done, so the three regimes now agree.
 */
export const FLOATING_PANEL_Z_INDEX = 999

/**
 * MYAC-1156 — the route names of the dedicated chat PAGES.
 *
 * On one of these the dock is not a second surface to open: it is the same
 * conversation the page already fills the screen with. Owned by the store
 * rather than by `GlobalChatPanel`, because the rule is about the panel's
 * state and every consumer has to agree on it.
 *
 * The saved-prompt pages (`*-chat-prompts`, `*-chat-prompt`, at /prompts since MYAC-1522) are
 * deliberately NOT here: they REPLACE the chat with a prompt library, so the
 * dock beside them is the conversation the member came from (MYAC-1140).
 */
export const CHAT_PAGE_ROUTES = ['qms-chat', 'products-chat']

/** Redesign (MYAC-1522, block 4) — the dashboard, whose left column is the conversation. */
export const DASHBOARD_ROUTE = 'dashboard'

/**
 * Redesign (MYAC-1522, block 3) — the routes where the conversation IS the page:
 * the dedicated chat pages, and Pilotage's conversation (`/conversation`; on the
 * legacy address, `?tab=chat` or no tab). The dock holds off there, « Ouvrir en
 * pleine page » leads there, and a reply that lands there is seen.
 */
export function isConversationPage(route: { name?: unknown, params?: Record<string, unknown>, query?: LocationQuery }): boolean {
  const routeName = String(route.name ?? '')
  if (CHAT_PAGE_ROUTES.includes(routeName)) return true
  // Block 4 — the dashboard hosts the conversation in its own assistant column:
  // the same rules as Pilotage's conversation (dock closed on arrival, handed the
  // exchange when one is under way on the way out). Since the onboarding pass
  // (MYAC-1522, decision H) the column is there with no project too.
  if (routeName === DASHBOARD_ROUTE) return true
  if (!isPilotageRoute(routeName) && !routeName.endsWith('-referentials')) return false
  return (tabOfLocation(route) ?? PILOTAGE_DEFAULT_TAB) === 'chat'
}

/**
 * MYAC-919 foundation — what page the user is on, structured. UX input ONLY:
 * it may drive suggestions/labels client-side and MAY later be attached to the
 * send path once a backend contract exists. It must NEVER become an
 * authorization input — the security boundary is the chat session IDENTITY
 * (`projects\<pid>`), validated server-side; never session state, never a
 * `state_delta` (same doctrine as companionSend's, see git history).
 */
export interface ChatPageContext {
  routeName: string
  projectId: string | null
  projectType: 'qms' | 'td' | null
  tab: string | null // e.g. the referentials ?tab= query
  params: Record<string, string>
}

const STORAGE_KEY = 'mac.chatPanel'

interface PersistedPanelState {
  width: number
  isOpen: boolean
}

/**
 * The opening width for a user who has never resized the panel: PREFERRED_WIDTH,
 * cut down to whatever leaves the page its MIN_CONTENT_WIDTH, then clamped to the
 * panel's own bounds. Reads the viewport directly because layoutWidth is not
 * measured until the panel mounts.
 */
export const preferredInitialWidth = (viewportWidth: number): number => {
  if (!viewportWidth) return DEFAULT_WIDTH
  return clampWidth(Math.min(PREFERRED_WIDTH, viewportWidth - MIN_CONTENT_WIDTH))
}

const loadPersisted = (): PersistedPanelState => {
  const initialWidth = preferredInitialWidth(
    typeof window !== 'undefined' ? window.innerWidth : 0,
  )
  const fallback: PersistedPanelState = { width: initialWidth, isOpen: false }
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return fallback
    const parsed = JSON.parse(raw) as Partial<PersistedPanelState>
    return {
      // A width the user chose is theirs; only an absent one is recomputed.
      width: typeof parsed.width === 'number' ? clampWidth(parsed.width) : initialWidth,
      // Persisted like a layout choice, not a popup — EXCEPT the takeover
      // regime (phone-width viewport), which never auto-reopens.
      isOpen: typeof parsed.isOpen === 'boolean' && window.innerWidth >= TAKEOVER_MAX_VIEWPORT
        ? parsed.isOpen
        : false,
    }
  } catch (error) {
    logger.debug('chatPanel: ignoring corrupted persisted state', error)
    return fallback
  }
}

const clampWidth = (width: number): number =>
  Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, Math.round(width)))

export const useChatPanelStore = defineStore('chatPanel', {
  state: () => ({
    // Redesign (MYAC-1522, block 3) — the assistant's live sign
    // (composables/useChatActivity.ts): what it is working on, and a reply that
    // landed out of sight.
    workingSubject: '' as string,
    isWorking: false,
    replyUnseen: false,
    /** The one-time « C'est prêt » unfolding of the edge tab. */
    readyNoticeOpen: false,
    layoutWidth: 0,
    // Mirrored from window.innerWidth on every layout measurement: the getter
    // below must NOT read window.innerWidth directly — it isn't reactive, and a
    // computed that returns from the takeover branch without touching any
    // reactive state ends up with zero dependencies and never recomputes
    // (observed: panel frozen in takeover after growing the window back).
    viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
    pageContext: null as ChatPageContext | null,
    // The z-index of the modal mask currently on screen, or 0 for none.
    // Observed from the DOM by GlobalChatPanel — see `elevated` / `liftZIndex`.
    maskZIndex: 0,
    // Is one of those masks a dialog the CHAT opened? Observed the same way.
    chatOwnedMask: false,
    // MYAC-1156 — the dock is not part of the dedicated chat page. Derived from
    // the route and deliberately NOT persisted: it takes the dock off the screen
    // for the visit without touching `isOpen`, which stays the member's own
    // layout choice.
    suppressed: false,
    // How many dialogs are on screen IN FULL SCREEN — MYAC-1287. A counter
    // rather than a flag: several `AppDialog`s are mounted at once (one per
    // host), and a boolean the second one writes on close would clear the
    // first one's. Not persisted: it describes what is on screen right now.
    fullScreenDialogs: 0,
    /**
     * MYAC-918 — the edge handle's « Vos conversations » is open: its own drawer,
     * over any page, the conversation pages included (the mockup, PR 868).
     */
    listOpen: false,
    ...loadPersisted(),
  }),
  getters: {
    /**
     * Does the panel need to paint ABOVE a dialog's mask?
     *
     * The panel is docked — in normal flow, pushing content aside — while a
     * modal mask is `position: fixed` on the body at `z-index: 1100`
     * (`@primevue/core/config`, `zIndex.modal`). Without lifting it, the mask
     * covers the panel and the user can see the chat but not touch it.
     *
     * Driven by the PRESENCE OF A MASK, not by dialogs announcing themselves.
     * The first version had each dialog claim and release; sixteen components
     * mount their own `<Dialog>` (MYAC-1134), so exactly the ones that had not
     * been taught still covered the chat. A condition the dialogs cannot forget
     * to satisfy is the only one that holds until that shell is shared.
     *
     * Still conditional: a permanently-lifted panel would outrank every other
     * overlay in the app for no reason.
     *
     * MYAC-1140 — and NOT when the dialog is one the chat itself opened. The
     * rule above is about a dialog on the page covering the chat; a dialog
     * launched from the chat (the saved-prompt viewer, editor and manager) is
     * the reverse, and lifting over it clipped it behind the panel with its
     * footer buttons unreachable. It cannot be solved by giving that dialog a
     * higher z-index — `liftZIndex` is measured from the top mask and adds one,
     * so the panel wins by construction — so the chat-owned case suppresses the
     * lift instead. Any page dialog underneath stays covered too, which is
     * right: it opened earlier and sits lower in PrimeVue's counter.
     */
    elevated(state): boolean {
      return state.maskZIndex > 0 && !state.chatOwnedMask
    },

    /**
     * Is a full-screen dialog sharing the row right now? — MYAC-1287.
     *
     * The docked panel reads this to fill the whole viewport instead of
     * starting under the app header: beside a dialog that runs edge to edge,
     * a panel one header short reads as a chat box that shrank (Sy, 2026-09-16).
     */
    hasFullScreenDialog(state): boolean {
      return state.fullScreenDialogs > 0
    },

    /**
     * One above whatever the mask actually got.
     *
     * NOT a constant. PrimeVue hands each overlay the next value from a running
     * counter, so the mask's z-index depends on how many overlays this page has
     * opened since load — measured live at 1101 on a fresh page and 2102 after a
     * few navigations. A fixed 1200 therefore worked on the tabs reached early
     * and silently lost on the ones reached later, which is exactly how this
     * read as "broken on Complétude and Continuité" rather than "broken".
     */
    liftZIndex(state): number {
      return state.maskZIndex + 1
    },
    /** The floating panel's z-index: lifted over a dialog's mask while one is
     * there, under the app's popups otherwise. */
    floatingZIndex(): number {
      return this.elevated ? this.liftZIndex : FLOATING_PANEL_Z_INDEX
    },
    /**
     * Is the dock actually on screen?
     *
     * `isOpen` is what the MEMBER asked for — persisted like a layout choice.
     * `suppressed` is what the PAGE allows. Everything that renders, measures,
     * or steps aside for the panel reads THIS; only the open/close gestures and
     * the persisted state read `isOpen` directly.
     */
    isVisible(state): boolean {
      return state.isOpen && !state.suppressed
    },
    // Content available beside the panel = the measured layout row width minus
    // the panel's own width (layoutWidth includes the panel when docked — see
    // GlobalChatPanel's ResizeObserver). Dragging the panel wider than
    // layoutWidth - MIN_CONTENT_WIDTH flips this from docked to overlay on its
    // own — that IS the "resize can never crush content" rule from the spec.
    regime(state): ChatPanelRegime {
      if (state.viewportWidth < TAKEOVER_MAX_VIEWPORT) return 'takeover'
      if (state.layoutWidth - state.width >= MIN_CONTENT_WIDTH) return 'docked'
      return 'overlay'
    },
  },
  actions: {
    // Fix-wave (whole-branch review, C2): `userInitiated` defaults true so every
    // existing caller (toggle, openWithDraft, openWithFocus, the launcher button)
    // stays counted unchanged. Only GlobalChatPanel's route watcher — which calls
    // open() as a navigation SIDE EFFECT when leaving the dedicated chat page with
    // messages present, not because the user asked to open anything — opts out
    // with `{ userInitiated: false }`. Without this, one visit to the chat page
    // produced two `chat_opened` events: one from ROUTE_ACTIONS on arrival, one
    // from this re-open on departure.
    open(opts: { userInitiated?: boolean } = {}) {
      const userInitiated = opts.userInitiated !== false
      // Count the TRANSITION, not the call: openWithFocus and toggle both land
      // here, and an already-open panel being re-opened is not a new session.
      // Measured on `isVisible` rather than `isOpen`, since a dock the chat page
      // is holding off screen is not open to the member looking at it.
      const wasVisible = this.isVisible
      // MYAC-1156 — nothing opens the dock on a dedicated chat page. Not the
      // panel itself, and NOT a member pressing the button either.
      //
      // The first cut let a deliberate press through, reading the ticket's «a
      // session started elsewhere stays reachable via the existing button» as
      // "keep the button working". Tested on the real app (Sy, 2026-09-01) that
      // produces two chats side by side — the very thing the ticket objected to.
      // The sentence is about not removing the way IN, and the chat page already
      // satisfies it: it renders the same conversation from the same store, so
      // the session is not lost, it is what fills the screen.
      //
      // So the hold is absolute while it lasts. It lifts on navigation, and only
      // there — see GlobalChatPanel's route watcher.
      if (this.suppressed) return
      this.isOpen = true
      this.persist()
      if (!wasVisible && userInitiated) {
        try {
          trackAction(CsmAction.ChatOpened, {
            // The chat's project (MYAC-1348), as ChatMessageSent does: the page's
            // project is empty on the dashboard, where the dock still has one.
            projectId: useChatScope().scopeProjectId.value || undefined,
          })
        } catch { /* tracking never breaks the panel */ }
      }
    },
    close() {
      this.isOpen = false
      this.persist()
    },
    /** MYAC-918 — the edge handle: open the person's conversations. */
    openList() {
      this.listOpen = true
    },
    closeList() {
      this.listOpen = false
    },
    toggle() {
      // `isVisible`, not `isOpen`: on a chat page the dock is held off screen,
      // and the member pressing the button there is asking for it back.
      if (this.isVisible) this.close()
      else this.open()
    },
    /**
     * A dialog beside the panel went full screen, or came back — MYAC-1287.
     *
     * Told rather than observed, unlike the mask below. The mask lift has to
     * hold for sixteen dialogs that know nothing about this panel, so it reads
     * the DOM; full screen is the shared shell's own state and no other dialog
     * can produce it, so there is nobody to forget to announce it.
     */
    setFullScreenDialog(open: boolean) {
      this.fullScreenDialogs = Math.max(0, this.fullScreenDialogs + (open ? 1 : -1))
    },
    setMaskZIndex(z: number) {
      this.maskZIndex = z
    },
    setChatOwnedMask(present: boolean) {
      this.chatOwnedMask = present
    },
    /**
     * MYAC-1156 — hold the dock off the screen while the member is on a
     * dedicated chat page.
     *
     * A hold rather than a `close()`, which is what shipped first and cost two
     * things of its own: `close()` persists, so a single visit to the chat page
     * silently forgot a dock the member deliberately kept open, and getting it
     * back on the way out then needed a `messages.length > 0` guess at whether
     * they had wanted it. Leaving `isOpen` alone means leaving simply hands back
     * whatever they had.
     */
    setSuppressed(suppressed: boolean) {
      this.suppressed = suppressed
    },
    setWidth(width: number) {
      this.width = clampWidth(width)
      this.persist()
    },
    // Drag-resize calls this on every pointermove — clamps + sets state WITHOUT
    // touching localStorage. setWidth (persisting) is called once in stopResize.
    setWidthTransient(width: number) {
      this.width = clampWidth(width)
    },
    setLayoutWidth(px: number) {
      this.layoutWidth = Math.round(px)
      // The layout element resizes whenever the window does, so this is the
      // one reliable place to refresh the reactive viewport mirror.
      this.viewportWidth = window.innerWidth
    },
    setPageContext(ctx: ChatPageContext) {
      this.pageContext = ctx
    },
    /** Redesign — a reply has started; `subject` is the person's own request, shortened. */
    markWorking(subject: string) {
      this.isWorking = true
      this.workingSubject = subject
    },
    /** The reply finished; out of sight, it waits as unseen and says so once. */
    markDone(outOfSight: boolean) {
      this.isWorking = false
      if (outOfSight) {
        this.replyUnseen = true
        this.readyNoticeOpen = true
      }
    },
    /** The conversation is in view: nothing is waiting any more. */
    markSeen() {
      this.replyUnseen = false
      this.readyNoticeOpen = false
    },
    /** « Plus tard »: the tab folds back; the dot stays until the reply is seen. */
    dismissReadyNotice() {
      this.readyNoticeOpen = false
    },
    /**
     * Open the chat and send `text` as the person's message — for a button whose
     * label IS the question (the dashboard's « ask the assistant to look in the
     * files »). The send goes through ChatInterface (`chatbotStore.pendingSend`),
     * so out of credits the text becomes a draft with the notice, never a paid
     * turn. On a page that hosts the conversation the dock stays put: the page's
     * own chat sends it.
     *
     * `focus` — MYAC-1166 — attaches the object the message is about, the way
     * `openWithFocus` does, so the reference travels with THIS message
     * (`sendMessage` takes it off the store as it sends). The task dialog's
     * « ask the assistant to draft it » is the caller: one click, one message,
     * about one task. It replaces any attachment already pending, as `setFocus`
     * always does — two references on one question is a question about neither.
     *
     * One action for both callers on purpose (front CLAUDE.md, « one store
     * action »): the credit and project holds live in ChatInterface's send path,
     * and a second entry point must inherit them rather than re-wire them.
     *
     * `opening` — MYAC-1522 (B3) — what MAC « just said » on the calling surface
     * when the chat it opens shows something else: the welcome letter, whose reply
     * opens a project chat greeting with its Pilotage summary. It rides this turn
     * only, and every call sets it, so no other caller inherits a stale letter.
     */
    openAndSend(text: string, focus?: ChatFocusRef | null, opening?: { message: string } | null) {
      const chatbot = useChatbotStore()
      if (focus) chatbot.setFocus(focus)
      chatbot.pendingOpening = opening ?? null
      chatbot.pendingSend = text
      // MYAC-1269 — the list shares the panel: what is sent must be on screen.
      this.listOpen = false
      this.open()
    },
    // Rides MYAC-750: ChatInterface's input is bound to chatbotStore.draftMessage.
    openWithDraft(text: string) {
      useChatbotStore().draftMessage = text
      this.listOpen = false
      this.open()
    },
    /**
     * MYAC-919 — open the chat ON something: the panel comes up with the object
     * attached (a chip above the input, plus questions worth asking about it),
     * and nothing is sent. Pressing the same item again detaches it.
     *
     * Deliberately NOT a question: pressing a «Discuss» button is navigation, and
     * navigation must not spend a credit. The reference is delivered with the
     * first message the user actually chooses to send — which is also the moment
     * it becomes worth anything — and is gone once it has been.
     */
    openWithFocus(focus: ChatFocusRef) {
      const chatbot = useChatbotStore()
      // Pressing the same item's button again takes it back off — one way out
      // beside the chip's own cross (MYAC-1531). The panel stays open:
      // the user asked for the chat, they just changed their mind about what
      // they wanted to point at.
      if (chatbot.pendingFocus && focusKey(chatbot.pendingFocus) === focusKey(focus)) {
        chatbot.clearFocus()
      } else {
        chatbot.setFocus(focus)
      }
      this.listOpen = false
      this.open()
    },
    persist() {
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ width: this.width, isOpen: this.isOpen } satisfies PersistedPanelState),
        )
      } catch (error) {
        logger.debug('chatPanel: failed to persist state', error)
      }
    },
  },
})
